import { EmptyState, ErrorState, TableSkeletonLoader } from "@repo/ui";
import {
  Button,
  Icon,
  InputField,
  TableSortable,
  Typography,
  type ColumnDataProps,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useRoles } from "./useRoles";

export default function SavRoleManagement() {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState("");
  const { data: roles, isLoading, error } = useRoles();

  const filteredRoles =
    roles?.filter(
      (role) =>
        role.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
        role.description?.toLowerCase().includes(searchValue.toLowerCase()),
    ) || [];

  const [columns, setColumns] = useState<ColumnDataProps[]>([
    {
      key: "name",
      label: intl.formatMessage({
        id: "/1srir",
        defaultMessage: "Name",
        description: "Table column header for role name",
      }),
      isChecked: true,
    },
    {
      key: "description",
      label: intl.formatMessage({
        id: "aDnvtL",
        defaultMessage: "Description",
        description: "Table column header for role description",
      }),
      isChecked: true,
    },
    {
      key: "type",
      label: intl.formatMessage({
        id: "ibOy6H",
        defaultMessage: "Type",
        description: "Table column header for role type",
      }),
      isChecked: true,
    },
    {
      key: "status",
      label: intl.formatMessage({
        id: "k2W6dA",
        defaultMessage: "Status",
        description: "Table column header for role status",
      }),
      isChecked: true,
    },
    {
      key: "lastUpdated",
      label: intl.formatMessage({
        id: "6DhLtb",
        defaultMessage: "Last Updated",
        description: "Table column header for role last updated date",
      }),
      isChecked: true,
    },
    {
      key: "actions",
      label: intl.formatMessage({
        id: "bwi84e",
        defaultMessage: "Actions",
        description: "Table column header for role actions",
      }),
      isChecked: true,
    },
  ]);

  const tableData = filteredRoles.map((role) => ({
    name: (
      <Typography kind="body2" color="--color-foreground-secondary-bold">
        {role.name}
      </Typography>
    ),
    description: (
      <Typography kind="body2" color="--color-foreground-secondary-bold">
        {role.description}
      </Typography>
    ),
    type: (
      <Typography kind="body2" color="--color-foreground-secondary-bold">
        {role.type}
      </Typography>
    ),
    status: (
      <Typography kind="body2" color="--color-foreground-secondary-bold">
        {role.status}
      </Typography>
    ),
    lastUpdated: (
      <Typography kind="body2" color="--color-foreground-secondary-bold">
        {new Date(role.lastUpdated).toLocaleDateString()}
      </Typography>
    ),
    actions: <Icon kind="MoreVertical" size="small" />,
  }));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Typography kind="h1">
          <FormattedMessage
            id="9fGmIP"
            defaultMessage="Sav Role Management"
            description="Title for Sav Role Management page"
          />
        </Typography>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <InputField
            name="search"
            placeholder="Search roles..."
            kind="search"
            value={searchValue}
            setValue={setSearchValue}
            prefixIcon={<Icon kind="Search" size="small" />}
          />
          <Button kind="outlined">
            <Icon kind="Filter" size="small" />
            <FormattedMessage
              id="Az3dMN"
              defaultMessage="Filters"
              description="Button to open filters for role list"
            />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button kind="filled">
            <FormattedMessage
              id="feKi94"
              defaultMessage="Add SAV Role"
              description="Button to create a new SAV role"
            />
          </Button>
          <Button kind="outlined">
            <Icon kind="Download" size="small" />
            <FormattedMessage
              id="8hJHSK"
              defaultMessage="Export"
              description="Button to export role data"
            />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeletonLoader rows={5} />
      ) : error ? (
        <ErrorState
          message={intl.formatMessage({
            id: "/IQaxj",
            defaultMessage: "Failed to load roles",
            description: "Error message when roles fail to load",
          })}
        />
      ) : tableData.length === 0 ? (
        <EmptyState
          message={intl.formatMessage({
            id: "F7qjQ0",
            defaultMessage: "No roles found",
            description: "Empty state message when no roles exist",
          })}
        />
      ) : (
        <TableSortable
          data={tableData}
          columns={columns}
          setColumns={setColumns}
          columnConfigs={{ isDndDisabled: true }}
          isVirtualizationEnabled={false}
        />
      )}
    </div>
  );
}
